import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import {
  Loader2, ShieldAlert, DollarSign, TrendingUp, Flag, Send, MapPin,
  Users, MessageSquare, Heart, ChevronRight, Crown, Download,
  Bell, AlertTriangle, XCircle, CreditCard, FileText, PieChart,
} from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

function StatCard({ label, value, sub, color = '#B91C1C', icon: Icon, big }) {
  return (
    <div className="bg-[var(--surface-2)] rounded-2xl p-3 border border-[var(--hairline)]">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} style={{ color }}/>}
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{label}</p>
      </div>
      <p className={`font-display ${big ? 'text-2xl' : 'text-xl'} font-[700] tabular-nums`} style={{ color }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-[10px] text-[#8A8A93] mt-0.5">{sub}</p>}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [tab, setTab] = useState('overview');
  const [revenue, setRevenue] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsFilter, setReportsFilter] = useState('open');
  const [subs, setSubs] = useState([]);
  const [subFilter, setSubFilter] = useState('premium');
  const [broadcastList, setBroadcastList] = useState([]);
  const [bForm, setBForm] = useState({ title: '', body: '', target: 'all', tier: 'premium', city: '' });
  const [sending, setSending] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [alertKind, setAlertKind] = useState('');
  const [fraudUnread, setFraudUnread] = useState(0);  // alt-account cluster badge

  useEffect(() => {
    api.get('/admin/me').then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  // Poll open alt-account fraud alert count every 60s so the sidebar badge
  // stays fresh without the admin having to refresh the page.
  useEffect(() => {
    if (!authChecked) return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get('/admin/alerts?category=alt_account_abuse&status=open&limit=1');
        if (alive) setFraudUnread((r.data.unread_by_category || {}).alt_account_abuse || (r.data.alerts || []).length || 0);
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [authChecked]);

  const loadOverview = useCallback(async () => {
    const [r, e, a] = await Promise.all([
      api.get('/admin/revenue'),
      api.get('/admin/engagement'),
      api.get(`/admin/alerts?limit=20${alertKind ? `&kind=${alertKind}` : ''}`),
    ]);
    setRevenue(r.data); setEngagement(e.data); setAlerts(a.data.alerts || []); setUnread(a.data.unread || 0);
  }, [alertKind]);

  const loadReports = useCallback(async () => {
    const r = await api.get(`/admin/reports?status=${reportsFilter}`);
    setReports(r.data.reports); setReportsTotal(r.data.total);
  }, [reportsFilter]);

  const loadSubs = useCallback(async () => {
    const r = await api.get(`/admin/subscriptions?tier=${subFilter}&limit=50`);
    setSubs(r.data.subscriptions);
  }, [subFilter]);

  const loadBroadcasts = useCallback(async () => {
    const r = await api.get('/admin/broadcasts');
    setBroadcastList(r.data.broadcasts);
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    if (tab === 'overview') loadOverview();
    else if (tab === 'reports') loadReports();
    else if (tab === 'subs') loadSubs();
    else if (tab === 'broadcast') loadBroadcasts();
  }, [authChecked, tab, loadOverview, loadReports, loadSubs, loadBroadcasts]);

  const resolveReport = async (r, action) => {
    const note = action === 'ban' ? window.prompt('Ban reason (optional):') || '' : '';
    if (action === 'ban' && note === null) return;
    try {
      await api.post(`/admin/reports/${r.report_id}/resolve`, { action, note });
      toast.success(action === 'ban' ? 'User banned' : 'Report dismissed');
      loadReports();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendBroadcast = async () => {
    if (!bForm.title.trim() || !bForm.body.trim()) { toast.error('Title and body required'); return; }
    if (!window.confirm(`Send "${bForm.title}" to ${bForm.target === 'all' ? 'ALL users' : bForm.target === 'tier' ? bForm.tier + ' users' : 'users in ' + bForm.city}?`)) return;
    setSending(true);
    try {
      const r = await api.post('/admin/broadcast', bForm);
      toast.success(`Sent to ${r.data.sent}/${r.data.recipient_count} users`);
      setBForm({ ...bForm, title: '', body: '' });
      loadBroadcasts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${BACKEND_URL}/api/admin/users.csv?status=active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'mtk_users_active.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]"/></div>;
  }

  return (
    <div className="min-h-screen bg-white pb-12" data-testid="admin-dashboard-page">
      <div className="px-5 pt-8 pb-3 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Admin</h1>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11}/> Admin
        </span>
      </div>

      {/* Tab pills */}
      <div className="px-5 pt-4 pb-3 flex gap-2 overflow-x-auto no-scrollbar">
        {[
          ['overview','Overview',TrendingUp],
          ['subs','Subscriptions',Crown],
          ['reports','Reports',Flag],
          ['broadcast','Broadcast',Send],
        ].map(([k,label,Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition inline-flex items-center gap-1.5 ${
              tab === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'
            }`}>
            <Icon size={11}/> {label}
          </button>
        ))}
        <Link to="/admin/users" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-users">
          <Users size={11}/> Users <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/analytics" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-analytics">
          <TrendingUp size={11}/> Analytics <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/financials" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-financials">
          <DollarSign size={11}/> Financials <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/ratios" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-ratios">
          <PieChart size={11}/> Ratios <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/scoring" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-scoring">
          <Heart size={11}/> Scoring <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/push-log" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-push-log">
          <Bell size={11}/> Push log <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/support" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] inline-flex items-center gap-1.5" data-testid="admin-tab-support">
          <MessageSquare size={11}/> Tickets <ChevronRight size={10}/>
        </Link>
        <Link to="/admin/fraud" className="flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border bg-white border-[var(--hairline-strong)] text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center gap-1.5" data-testid="admin-tab-fraud">
          <ShieldAlert size={11}/> Fraud
          {fraudUnread > 0 && (
            <span data-testid="admin-fraud-badge" className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#B91C1C] text-white text-[9px] font-bold">
              {fraudUnread > 99 ? '99+' : fraudUnread}
            </span>
          )}
          <ChevronRight size={10}/>
        </Link>
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="px-5 space-y-5">
          {!revenue || !engagement ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]"/></div>
          ) : (
            <>
              {/* Revenue */}
              <section>
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] mb-2 flex items-center gap-1.5"><DollarSign size={11}/> Revenue</h2>
                <div className="grid grid-cols-2 gap-2">
                  <StatCard label="MRR" value={`$${revenue.mrr.toLocaleString()}`} sub={`ARR: $${revenue.arr.toLocaleString()}`} color="#15803D" icon={DollarSign} big/>
                  <StatCard label="ARPU" value={`$${revenue.arpu.toFixed(2)}`} sub={`${revenue.paid_total} paid users`} color="#3B82F6" icon={Users} big/>
                  <StatCard label="Premium" value={revenue.by_tier.premium} sub={`$${revenue.tier_prices.premium}/mo each`} color="#B91C1C" icon={Crown}/>
                  <StatCard label="VIP" value={revenue.by_tier.vip} sub={`$${revenue.tier_prices.vip}/mo each`} color="#B38138" icon={Crown}/>
                  <StatCard label="New Paid · 7d" value={revenue.new_paid_7d} color="#15803D" icon={TrendingUp}/>
                  <StatCard label="Churned · 30d" value={revenue.churned_30d} color="#8A8A93" icon={Flag}/>
                </div>
              </section>

              {/* Engagement */}
              <section>
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] mb-2 flex items-center gap-1.5"><TrendingUp size={11}/> Engagement</h2>
                <div className="grid grid-cols-3 gap-2">
                  <StatCard label="DAU" value={engagement.dau} color="#B91C1C" icon={Users}/>
                  <StatCard label="WAU" value={engagement.wau} color="#3B82F6" icon={Users}/>
                  <StatCard label="MAU" value={engagement.mau} color="#15803D" icon={Users}/>
                </div>
              </section>

              {/* Funnel */}
              <section>
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] mb-2">30-day funnel</h2>
                <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)] space-y-2">
                  {[
                    ['Signups', engagement.funnel_30d.signups],
                    ['Profile complete', engagement.funnel_30d.completed_profile],
                    ['First swipe', engagement.funnel_30d.first_swipe],
                    ['First match', engagement.funnel_30d.first_match],
                    ['First message', engagement.funnel_30d.first_message],
                  ].map(([label, val], i, arr) => {
                    const max = arr[0][1] || 1;
                    const pct = Math.round((val / max) * 100);
                    return (
                      <div key={label} className="flex items-center gap-3">
                        <span className="text-[12px] text-[#4B4B52] flex-1 font-medium">{label}</span>
                        <div className="flex-[2] h-2 bg-black/[0.06] rounded-full overflow-hidden">
                          <div className="h-full brand-gradient transition-all" style={{ width: `${pct}%` }}/>
                        </div>
                        <span className="w-20 text-right text-[12px] font-bold tabular-nums">{val.toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* 24h activity */}
              <section>
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] mb-2">Last 24 hours</h2>
                <div className="grid grid-cols-2 gap-2">
                  <StatCard label="Swipes" value={engagement.activity_24h.swipes} color="#B91C1C" icon={Heart}/>
                  <StatCard label="Matches" value={engagement.activity_24h.matches} color="#15803D" icon={Heart}/>
                  <StatCard label="Messages" value={engagement.activity_24h.messages} color="#3B82F6" icon={MessageSquare}/>
                  <StatCard label="Intros" value={engagement.activity_24h.intros} color="#B38138" icon={Send}/>
                </div>
              </section>

              {/* Admin Alerts inbox */}
              <AlertsPanel alerts={alerts} kind={alertKind} setKind={setAlertKind} unread={unread}
                onMarkRead={async (id) => {
                  try {
                    await api.post(`/admin/alerts/${id}/read`);
                    setAlerts(prev => prev.map(a => a.alert_id === id ? { ...a, read_at: new Date().toISOString() } : a));
                    setUnread(u => Math.max(0, u - 1));
                  } catch { /* swallow */ }
                }}
                onMarkAllRead={async () => {
                  try {
                    await api.post('/admin/alerts/read-all');
                    setAlerts(prev => prev.map(a => a.read_at ? a : { ...a, read_at: new Date().toISOString() }));
                    setUnread(0);
                  } catch { /* swallow */ }
                }}
              />

              <button onClick={downloadCsv} data-testid="export-csv"
                className="w-full py-3 rounded-full border border-[var(--hairline-strong)] text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-black/[0.02] transition">
                <Download size={14}/> Export active users to CSV
              </button>
            </>
          )}
        </div>
      )}

      {/* SUBSCRIPTIONS */}
      {tab === 'subs' && (
        <div className="px-5">
          <div className="flex gap-2 mb-3">
            {[['premium','Premium'],['vip','VIP'],['free','Free']].map(([k,l]) => (
              <button key={k} onClick={() => setSubFilter(k)} data-testid={`sub-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                  subFilter === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'
                }`}>{l}</button>
            ))}
          </div>
          {subs.length === 0 ? (
            <p className="text-center text-[#8A8A93] py-12 text-sm">No {subFilter} subscriptions</p>
          ) : (
            <div className="divide-y divide-[var(--hairline)]" data-testid="admin-subs-list">
              {subs.map(s => (
                <div key={s.user_id} className="py-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--surface-2)] flex-shrink-0 overflow-hidden">
                    {s.user?.photo_url && <img src={s.user.photo_url.startsWith('http') ? s.user.photo_url : `${BACKEND_URL}/api/files/${s.user.photo_url}`} alt="" className="w-full h-full object-cover" onError={e => e.target.style.display='none'}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[14px] truncate">{s.user?.display_name || '(deleted)'}</p>
                    <p className="text-[11px] text-[#8A8A93] truncate">{s.user?.email} · started {s.started_at?.slice(0,10)}</p>
                  </div>
                  <span className="badge gold-gradient text-white border-transparent">{s.tier}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* REPORTS */}
      {tab === 'reports' && (
        <div className="px-5">
          <div className="flex gap-2 mb-3">
            {[['open','Open'],['resolved','Resolved'],['all','All']].map(([k,l]) => (
              <button key={k} onClick={() => setReportsFilter(k)} data-testid={`reports-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                  reportsFilter === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'
                }`}>{l}</button>
            ))}
            <span className="ml-auto self-center text-[11px] text-[#8A8A93]">{reportsTotal} total</span>
          </div>
          {reports.length === 0 ? (
            <p className="text-center text-[#8A8A93] py-12 text-sm">All clear ✨</p>
          ) : (
            <div className="space-y-3" data-testid="admin-reports-list">
              {reports.map(r => (
                <div key={r.report_id} className="border border-[var(--hairline)] rounded-2xl p-3 bg-white">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px]">
                        <span className="font-semibold">{r.reporter?.display_name || 'A user'}</span>
                        <span className="text-[#8A8A93]"> reported </span>
                        <Link to={`/admin/users?search=${encodeURIComponent(r.reported?.user_id || '')}`}
                          className="font-semibold text-[#B91C1C] hover:underline">{r.reported?.display_name || 'unknown'}</Link>
                        {(r.reported?.report_count || 0) > 1 && <span className="ml-1 badge bg-[#FEF2F2] text-[#B91C1C] border-[#B91C1C]/20">{r.reported.report_count} total</span>}
                      </p>
                      <p className="text-[12px] text-[#4B4B52] mt-1">Reason: <span className="font-semibold">{r.reason}</span></p>
                      {r.description && <p className="text-[12px] text-[#8A8A93] mt-1 italic">"{r.description}"</p>}
                      <p className="text-[10px] text-[#8A8A93] mt-1">{r.created_at?.slice(0,16).replace('T',' ')}</p>
                    </div>
                    <span className={`badge ${r.status === 'resolved' ? 'bg-black/[0.05] text-[#4B4B52] border-black/10' : 'bg-[#FEF2F2] text-[#B91C1C] border-[#B91C1C]/20'}`}>
                      {r.status === 'resolved' ? r.resolution || 'Resolved' : 'Open'}
                    </span>
                  </div>
                  {r.status !== 'resolved' && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => resolveReport(r, 'dismiss')} data-testid={`dismiss-${r.report_id}`}
                        className="flex-1 py-2 rounded-full text-[11px] font-semibold border border-[var(--hairline-strong)] hover:bg-black/[0.02] transition">Dismiss</button>
                      <button onClick={() => resolveReport(r, 'ban')} data-testid={`ban-${r.report_id}`}
                        className="flex-1 py-2 rounded-full text-[11px] font-semibold brand-gradient text-white hover:scale-[1.01] transition">Ban user</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* BROADCAST */}
      {tab === 'broadcast' && (
        <div className="px-5 space-y-4">
          <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)] space-y-3">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-[#8A8A93]">Target</label>
              <div className="flex gap-2 mt-1.5">
                {[['all','All users'],['tier','By tier'],['city','By city']].map(([k,l]) => (
                  <button key={k} onClick={() => setBForm({...bForm, target: k})} data-testid={`bt-${k}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border ${
                      bForm.target === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52]'
                    }`}>{l}</button>
                ))}
              </div>
            </div>
            {bForm.target === 'tier' && (
              <select value={bForm.tier} onChange={e => setBForm({...bForm, tier: e.target.value})}
                className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm" data-testid="bt-tier">
                <option value="premium">Premium</option>
                <option value="vip">VIP</option>
                <option value="free">Free</option>
              </select>
            )}
            {bForm.target === 'city' && (
              <input value={bForm.city} onChange={e => setBForm({...bForm, city: e.target.value})}
                placeholder="City name (e.g. Toronto, ON)" data-testid="bt-city"
                className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm"/>
            )}
            <input value={bForm.title} onChange={e => setBForm({...bForm, title: e.target.value.slice(0,80)})}
              placeholder="Title · max 80 chars" data-testid="bt-title"
              className="w-full h-10 px-3 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm"/>
            <textarea value={bForm.body} onChange={e => setBForm({...bForm, body: e.target.value.slice(0,160)})}
              placeholder="Body · max 160 chars" rows={3} data-testid="bt-body"
              className="w-full px-3 py-2 rounded-xl border border-[var(--hairline-strong)] bg-white text-sm resize-none"/>
            <button onClick={sendBroadcast} disabled={sending || !bForm.title.trim() || !bForm.body.trim()}
              data-testid="send-broadcast"
              className="w-full py-3 rounded-full brand-gradient text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 brand-glow-sm hover:scale-[1.01] transition-all">
              {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Send broadcast
            </button>
          </div>

          <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] pt-2">Recent broadcasts</h2>
          <div className="divide-y divide-[var(--hairline)]" data-testid="broadcast-history">
            {broadcastList.length === 0 ? (
              <p className="text-center text-[#8A8A93] py-8 text-sm">No broadcasts yet</p>
            ) : (
              broadcastList.map(b => (
                <div key={b.broadcast_id} className="py-3">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-semibold text-[13px]">{b.title}</p>
                    <span className="text-[10px] text-[#8A8A93] ml-auto">{b.created_at?.slice(0,10)}</span>
                  </div>
                  <p className="text-[12px] text-[#4B4B52]">{b.body}</p>
                  <p className="text-[10px] text-[#8A8A93] mt-1">
                    Target: <span className="font-semibold">{b.target}{b.tier ? ` · ${b.tier}` : ''}{b.city ? ` · ${b.city}` : ''}</span>
                    {' · '}Sent: <span className="font-semibold">{b.sent}/{b.recipient_count}</span>
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


const ALERT_KINDS = [
  { kind: '', label: 'All', icon: Bell, color: '#4B4B52' },
  { kind: 'new_subscription', label: 'New subs', icon: Crown, color: '#15803D' },
  { kind: 'cancellation', label: 'Cancels', icon: XCircle, color: '#B91C1C' },
  { kind: 'payment_failed', label: 'Payment fail', icon: CreditCard, color: '#B38138' },
  { kind: 'user_report', label: 'Reports', icon: AlertTriangle, color: '#B91C1C' },
  { kind: 'daily_digest', label: 'Digests', icon: FileText, color: '#3B82F6' },
];

function alertMeta(kind) {
  return ALERT_KINDS.find(k => k.kind === kind) || { icon: Bell, color: '#4B4B52', label: kind || 'Alert' };
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function AlertsPanel({ alerts, kind, setKind, unread = 0, onMarkRead, onMarkAllRead }) {
  const navigate = useNavigate();
  const linkFor = (a) => {
    const uid = a.meta?.user_id;
    if (uid && ['new_subscription', 'cancellation', 'payment_failed', 'user_report'].includes(a.kind)) {
      return `/admin/users/${uid}`;
    }
    if (a.kind === 'daily_digest') return '/admin/analytics';
    return null;
  };
  const handleRowClick = (a, link) => {
    if (!a.read_at) onMarkRead?.(a.alert_id);
    if (link) navigate(link);
  };
  return (
    <section data-testid="alerts-panel">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#8A8A93] flex items-center gap-1.5">
          <span className="relative inline-flex">
            <Bell size={11} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full bg-[#B91C1C] text-white text-[8px] font-bold tabular-nums leading-none"
                data-testid="alerts-unread-badge">
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </span>
          Admin alerts
        </h2>
        {unread > 0 && (
          <button onClick={onMarkAllRead} data-testid="mark-all-read"
            className="ml-auto text-[10px] font-bold text-[#B91C1C] hover:text-[#7F1D1D] uppercase tracking-wider transition">
            Mark all read
          </button>
        )}
        {unread === 0 && (
          <span className="ml-auto text-[10px] text-[#8A8A93]">all caught up</span>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 mb-2">
        {ALERT_KINDS.map(({ kind: k, label, icon: Icon, color }) => (
          <button key={k || 'all'} onClick={() => setKind(k)} data-testid={`alert-filter-${k || 'all'}`}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition inline-flex items-center gap-1 ${
              kind === k ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'
            }`}>
            <Icon size={10} style={{ color: kind === k ? '#fff' : color }} />
            {label}
          </button>
        ))}
      </div>
      <div className="bg-[var(--surface-2)] rounded-2xl border border-[var(--hairline)] overflow-hidden">
        {alerts.length === 0 ? (
          <p className="text-center text-[#8A8A93] py-8 text-[12px]">
            {kind ? `No ${alertMeta(kind).label.toLowerCase()} in the last 20 events.` : 'No admin alerts yet. They appear here when subscriptions change, reports come in, or the daily digest runs.'}
          </p>
        ) : (
          <div className="divide-y divide-[var(--hairline)]" data-testid="alerts-list">
            {alerts.map((a) => {
              const m = alertMeta(a.kind);
              const Icon = m.icon;
              const link = linkFor(a);
              const isUnread = !a.read_at;
              const interactive = !!link || isUnread;
              return (
                <div key={a.alert_id}
                  className={`px-3 py-2.5 flex items-start gap-2.5 transition relative ${interactive ? 'cursor-pointer hover:bg-black/[0.03] active:bg-black/[0.05]' : ''} ${isUnread ? 'bg-[#FEF2F2]/40' : ''}`}
                  onClick={interactive ? () => handleRowClick(a, link) : undefined}
                  data-testid={`alert-${a.alert_id}`}>
                  {isUnread && <span className="absolute left-0.5 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r bg-[#B91C1C]" data-testid="unread-indicator" />}
                  <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: m.color + '15', color: m.color }}>
                    <Icon size={12} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[12px] truncate ${isUnread ? 'font-bold text-[#0F0F10]' : 'font-semibold text-[#4B4B52]'}`}>{a.subject}</p>
                    <p className="text-[10px] text-[#8A8A93] mt-0.5 flex items-center gap-2 flex-wrap">
                      <span style={{ color: m.color }} className="font-bold uppercase tracking-wider">{m.label}</span>
                      <span>·</span>
                      <span>{relativeTime(a.created_at)}</span>
                      {!isUnread && <><span>·</span><span className="italic opacity-70">read</span></>}
                    </p>
                  </div>
                  {isUnread && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onMarkRead?.(a.alert_id); }}
                      className="w-6 h-6 rounded-full hover:bg-black/[0.06] flex items-center justify-center text-[#8A8A93] hover:text-[#4B4B52] transition flex-shrink-0"
                      title="Dismiss"
                      data-testid={`dismiss-${a.alert_id}`}>
                      <XCircle size={12} />
                    </button>
                  )}
                  {!isUnread && link && <ChevronRight size={13} className="text-[#A1A1AA] flex-shrink-0 mt-1.5" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
